/**
 * RcConnectionsPanel — admin dashboard tile for Bundle 2 OAuth adoption.
 *
 * Lives inside Reports → "RC Connections" tab. Surfaces per-rep RC OAuth
 * status, default DID, last call timestamp, and 7-day call/SMS volume so
 * admins can see who's actually using the new per-rep dialing path vs
 * still holding back. Admins can force-disconnect a stale rep (e.g. when
 * someone leaves the company) with one click.
 *
 * Auto-refreshes every 30s while mounted so newly connected reps light up
 * within seconds of completing OAuth in Settings.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Phone, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2,
  MessageSquare, PhoneCall, Search, LogOut, ShieldCheck,
} from "lucide-react";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function RcConnectionsPanel() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [showConnectedOnly, setShowConnectedOnly] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/rc/connections`, { headers });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load RC connections");
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), 30_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const forceDisconnect = async (userId, email) => {
    if (!window.confirm(`Force-disconnect RingCentral for ${email}?\n\nThe rep will be prompted to reconnect on their next Settings visit.`)) {
      return;
    }
    setBusyUserId(userId);
    try {
      await axios.post(`${API}/admin/rc/connections/${userId}/force-disconnect`, {}, { headers });
      toast.success(`Disconnected ${email}`);
      fetchData(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
    setBusyUserId(null);
  };

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = query.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (showConnectedOnly && !r.oauth_connected) return false;
      if (!q) return true;
      return (
        (r.email || "").toLowerCase().includes(q) ||
        (r.full_name || "").toLowerCase().includes(q) ||
        (r.owner_email || "").toLowerCase().includes(q) ||
        (r.rc_from_number || "").toLowerCase().includes(q)
      );
    });
  }, [data, query, showConnectedOnly]);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "var(--text-tertiary)" }}>
        <Loader2 style={{ width: 22, height: 22, animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
        <div style={{ fontSize: 12 }}>Loading RC connections…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: 10, color: "#991b1b", fontSize: 13 }} data-testid="rc-connections-error">
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load connections</div>
        <div style={{ fontSize: 12 }}>{error}</div>
      </div>
    );
  }

  const s = data?.summary || {};
  const adoptionPct = s.total_users ? Math.round((s.connected / s.total_users) * 100) : 0;

  return (
    <div data-testid="rc-connections-panel">
      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 14 }}>
        <SummaryTile
          label="Connected reps"
          value={`${s.connected} / ${s.total_users}`}
          sub={`${adoptionPct}% adoption`}
          icon={CheckCircle2}
          color="#10b981"
          dataTestId="rc-summary-connected"
        />
        <SummaryTile
          label="Dialing (last 7d)"
          value={s.dialing_last_7d || 0}
          sub="reps with ≥1 call"
          icon={PhoneCall}
          color="#2563eb"
          dataTestId="rc-summary-dialing"
        />
        <SummaryTile
          label="Calls (last 7d)"
          value={s.calls_last_7d || 0}
          sub="outbound RingOut"
          icon={Phone}
          color="#7c3aed"
          dataTestId="rc-summary-calls"
        />
        <SummaryTile
          label="SMS (last 7d)"
          value={s.sms_last_7d || 0}
          sub="outbound texts"
          icon={MessageSquare}
          color="#0891b2"
          dataTestId="rc-summary-sms"
        />
        <SummaryTile
          label="Config"
          value={s.oauth_configured ? "OAuth ✓" : "JWT only"}
          sub={s.jwt_configured ? "JWT fallback ready" : "no fallback"}
          icon={ShieldCheck}
          color={s.oauth_configured ? "#10b981" : "#f59e0b"}
          dataTestId="rc-summary-config"
        />
      </div>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "0 1 280px" }}>
          <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "#94a3b8" }} />
          <input
            data-testid="rc-connections-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, owner, DID…"
            style={{
              width: "100%", padding: "6px 10px 6px 30px", fontSize: 12,
              border: "0.5px solid #d2d2d7", borderRadius: 8, fontFamily: "inherit",
            }}
          />
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "#475569", cursor: "pointer" }}>
          <input
            type="checkbox"
            data-testid="rc-connections-only-connected"
            checked={showConnectedOnly}
            onChange={(e) => setShowConnectedOnly(e.target.checked)}
          />
          Connected only
        </label>
        <button
          onClick={() => fetchData()}
          disabled={refreshing}
          data-testid="rc-connections-refresh"
          style={{
            marginLeft: "auto", padding: "6px 12px", fontSize: 11, fontWeight: 600,
            background: "#fff", border: "0.5px solid #d2d2d7", borderRadius: 8,
            cursor: refreshing ? "wait" : "pointer", display: "inline-flex",
            alignItems: "center", gap: 6, fontFamily: "inherit", color: "#475569",
          }}
        >
          {refreshing
            ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} />
            : <RefreshCw style={{ width: 11, height: 11 }} />}
          Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }} data-testid="rc-connections-table">
            <thead>
              <tr style={{ background: "#fafafa", textAlign: "left", color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <th style={{ padding: "10px 12px" }}>Rep</th>
                <th style={{ padding: "10px 12px" }}>Status</th>
                <th style={{ padding: "10px 12px" }}>RC Owner</th>
                <th style={{ padding: "10px 12px" }}>Default DID</th>
                <th style={{ padding: "10px 12px", textAlign: "right" }}>Calls (7d)</th>
                <th style={{ padding: "10px 12px", textAlign: "right" }}>SMS (7d)</th>
                <th style={{ padding: "10px 12px" }}>Last call</th>
                <th style={{ padding: "10px 12px" }}>Connected</th>
                <th style={{ padding: "10px 12px", textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                  No reps match the current filter.
                </td></tr>
              )}
              {filteredRows.map((r) => (
                <tr key={r.user_id} style={{ borderTop: "0.5px solid #f1f5f9" }} data-testid={`rc-row-${r.user_id}`}>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.full_name || "—"}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.email}</div>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {r.oauth_connected ? (
                      r.token_expired ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 10, background: "#FFF3CD", color: "#856404", fontSize: 10, fontWeight: 600 }} data-testid={`rc-status-expired-${r.user_id}`}>
                          <AlertTriangle style={{ width: 10, height: 10 }} /> Token expired
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 10, background: "#DAFBE8", color: "#1D9E75", fontSize: 10, fontWeight: 600 }} data-testid={`rc-status-connected-${r.user_id}`}>
                          <CheckCircle2 style={{ width: 10, height: 10 }} /> Connected
                        </span>
                      )
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 10, background: "#f1f5f9", color: "#64748b", fontSize: 10, fontWeight: 600 }} data-testid={`rc-status-disconnected-${r.user_id}`}>
                        <XCircle style={{ width: 10, height: 10 }} /> Not connected
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#475569" }}>
                    {r.owner_email || r.owner_name || <span style={{ color: "#cbd5e1" }}>—</span>}
                    {r.extension_id && (
                      <div style={{ fontSize: 9, color: "#94a3b8", fontFamily: "monospace" }}>ext {r.extension_id}</div>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 11, color: "#334155" }}>
                    {r.rc_from_number || <span style={{ color: "#cbd5e1", fontFamily: "inherit" }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: r.calls_7d ? "#0f172a" : "#cbd5e1" }}>
                    {r.calls_7d || 0}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: r.sms_7d ? "#0f172a" : "#cbd5e1" }}>
                    {r.sms_7d || 0}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#64748b" }} title={fmtDateTime(r.last_call_at)}>
                    {fmtRelative(r.last_call_at)}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#64748b" }} title={fmtDateTime(r.connected_at)}>
                    {r.oauth_connected ? fmtRelative(r.connected_at) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    {r.oauth_connected && (
                      <button
                        onClick={() => forceDisconnect(r.user_id, r.email)}
                        disabled={busyUserId === r.user_id}
                        data-testid={`rc-force-disconnect-${r.user_id}`}
                        title="Force-disconnect this rep's RingCentral"
                        style={{
                          background: "#fff", border: "0.5px solid #ffccc7",
                          color: "#A32D2D", borderRadius: 6, padding: "4px 8px",
                          fontSize: 10, fontWeight: 600, cursor: busyUserId === r.user_id ? "wait" : "pointer",
                          display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "inherit",
                        }}
                      >
                        {busyUserId === r.user_id
                          ? <Loader2 style={{ width: 9, height: 9, animation: "spin 1s linear infinite" }} />
                          : <LogOut style={{ width: 9, height: 9 }} />}
                        Disconnect
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: "#94a3b8" }}>
        Auto-refreshes every 30s. Showing {filteredRows.length} of {data?.rows?.length || 0} reps.
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, icon: Icon, color, dataTestId }) {
  return (
    <div data-testid={dataTestId} style={{
      background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10,
      padding: "12px 14px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 10, right: 10, width: 28, height: 28, borderRadius: 8, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon style={{ width: 14, height: 14, color }} />
      </div>
      <div style={{ fontSize: 9, fontWeight: 700, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
